import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

export default function PortfolioPage() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set(projects.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [projects]);

  const filtered = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="portfolio-page">
      <section className="relative overflow-hidden px-6 pb-14 pt-20 md:pb-20 md:pt-28">
        <div className="absolute inset-0 radial-glow" />
        <div className="relative mx-auto max-w-7xl">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-fuchsia-400">Portfolio</p>
          <h1 className="mt-4 text-5xl font-bold tracking-tighter text-white md:text-7xl">
            Work we're <span className="gradient-text">proud of.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-white/70">
            A selection of brands, websites, and campaigns we've shipped with ambitious teams.
          </p>
        </div>
      </section>

      <section className="px-6 pb-6">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${
                filter === c
                  ? "border-white/30 bg-white/10 text-white"
                  : "border-white/10 text-white/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-2">
          {filtered.map((p) => (
            <Link
              key={p.id}
              to={`/portfolio/${p.slug}`}
              data-testid={`portfolio-card-${p.slug}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#131B2F] hover-lift"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={p.cover_image}
                  alt={p.title}
                  className="h-full w-full object-cover opacity-80 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-fuchsia-300">{p.category}</span>
                  <span className="text-xs text-white/40">{p.year}</span>
                </div>
                <h3 className="mt-2 text-2xl font-semibold text-white">{p.title}</h3>
                <p className="mt-2 text-sm text-white/60">{p.summary}</p>
                {p.tags?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span key={t} className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] text-white/60">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
